/**
 * KAN-746: корзины и заказы интернет-магазина (раздел "Заказы").
 *
 *   GET   /api/v1/orders/?tab=&search=&manager=&client=&page=&page_size=
 *   GET   /api/v1/orders/counts/
 *   GET   /api/v1/orders/<id>/
 *   POST  /api/v1/orders/ {client_id}
 *   PATCH /api/v1/orders/<id>/items/ {op: add|set_quantity|remove, ...}
 *   POST  /api/v1/orders/<id>/place/ | approve/ | cancel/
 *
 * Носитель заказа на backend - смета с mode=ecommerce: DRAFT = корзина,
 * PENDING_REVIEW = оформлен, APPROVED = подтверждён, CANCELLED = отменён.
 * Организация не в режиме интернет-магазина получает 404.
 */
import type { CatalogId } from './catalog';
import type { ManagerBrief } from './manager';

/** Вкладка списка заказов. */
export type OrderTab = 'carts' | 'placed' | 'approved' | 'cancelled';

export const ORDER_TABS: readonly OrderTab[] = ['carts', 'placed', 'approved', 'cancelled'];

/** tab=all: все статусы без пустых черновиков (блок заказов клиента). */
export type OrderListTab = OrderTab | 'all';

/** Статус заказа (статус сметы-носителя). */
export type OrderStatus = 'DRAFT' | 'PENDING_REVIEW' | 'APPROVED' | 'CANCELLED' | 'REJECTED';

export interface OrderClientBrief {
  id: string;
  name: string;
  company?: string | null;
}

/** Строка списка заказов. */
export interface OrderListItem {
  id: string;
  short_id: number | string | null;
  status: OrderStatus;
  client: OrderClientBrief | null;
  manager: ManagerBrief | null;
  items_count: number;
  total: number | string;
  currency: string | null;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  /** KAN-755: скидка заказа; старый backend поле не шлёт. */
  discount?: number | string | null;
  /** Брошенная корзина (клиенту уже напоминали). */
  abandoned: boolean;
}

export interface OrdersListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: OrderListItem[];
}

export interface OrdersListParams {
  tab: OrderListTab;
  search?: string;
  /** id менеджера или 'none' (без менеджера). */
  manager?: string;
  client?: string;
  page?: number;
  page_size?: number;
}

export type OrderCounts = Record<OrderTab, number>;

/** Позиция заказа. product_id нет у старых позиций и товаров не из каталога. */
export interface OrderItem {
  index: number;
  product_id: CatalogId | null;
  sku: string | null;
  name: string;
  quantity: number;
  price: number | string | null;
  total: number | string | null;
  /** Цены нет в каталоге: "цена по запросу". */
  price_unconfirmed: boolean;
  picture_url: string | null;
  url: string | null;
  /** KAN-755: скидка на строку (0: нет); total остаётся без скидки. */
  discount?: number | string | null;
  /** Название применённой скидки. */
  discount_name?: string | null;
}

/** Что текущему пользователю можно делать с заказом. */
export interface OrderPermissions {
  edit: boolean;
  place: boolean;
  approve: boolean;
  cancel: boolean;
}

export interface OrderDetail {
  id: string;
  short_id: number | string | null;
  status: OrderStatus;
  client: OrderClientBrief | null;
  manager: ManagerBrief | null;
  /** Автор заказа (id пользователя): менять менеджера может он и владелец. */
  created_by: string | null;
  items: OrderItem[];
  subtotal: number | string | null;
  delivery: number | string | null;
  markup: number | string | null;
  total: number | string | null;
  currency: string | null;
  address: string | null;
  created_at: string;
  submitted_at: string | null;
  reviewed_at: string | null;
  permissions: OrderPermissions;
  /** KAN-755: сумма скидок заказа (0: нет). */
  discount?: number | string | null;
  /** Применённые скидки с суммой. */
  discounts?: OrderDiscount[];
  /** Промокод заказа или null. */
  promo_code?: string | null;
  promo_status?: OrderPromoStatus | null;
}

/** KAN-755: результат промокода: применён, уже есть скидка больше, не подходит к товарам. */
export type OrderPromoStatus = 'applied' | 'not_better' | 'no_items';

export interface OrderDiscount {
  id: string;
  name: string;
  amount: number | string;
}

/** Правка позиций: добавить товар, изменить количество, удалить. */
export type OrderItemsPatch =
  | { op: 'add'; product_id: CatalogId; quantity: number }
  | { op: 'set_quantity'; index: number; quantity: number }
  | { op: 'remove'; index: number };

/** Переход статуса заказа. */
export type OrderAction = 'place' | 'approve' | 'cancel';
